import { useState, useEffect } from "react";
import { useCandidateAuth } from "@/context/CandidateAuthContext";
import { useNavigate } from "react-router-dom";

export default function CandidateCvPage() {
  const { candidateApi, logout } = useCandidateAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    candidateApi({ method: "get", url: "/candidate/cv" })
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [candidateApi]);

  const handleValidate = async () => {
    setValidating(true);
    try {
      await candidateApi({ method: "post", url: "/candidate/cv/validate" });
      setValidated(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur de validation");
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-red-300 text-center">
        <p className="font-semibold mb-2">Erreur</p>
        <p className="text-sm">{error}</p>
        <button
          onClick={() => { logout(); navigate("/candidat/login"); }}
          className="mt-4 text-sm text-violet-400 hover:text-violet-300"
        >
          Retour à la connexion
        </button>
      </div>
    );
  }

  const candidate = data?.candidate || {};
  const cvDraft = data?.cv_draft;
  const alreadyValidated = validated || data?.validated_by_candidate;

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-[#14183a] border border-slate-700/50 rounded-2xl p-6">
        <h1 className="text-2xl font-bold text-white mb-1">
          Bonjour {candidate.first_name} 👋
        </h1>
        <p className="text-slate-400 text-sm">
          Votre équipe Lenvia a préparé votre CV. Veuillez le vérifier et le valider ci-dessous.
        </p>
      </div>

      {/* CV data preview */}
      {cvDraft ? (
        <div className="bg-[#14183a] border border-slate-700/50 rounded-2xl p-6 space-y-4">
          <h2 className="font-semibold text-white text-lg">Votre CV</h2>

          {cvDraft.full_name && (
            <div>
              <span className="text-slate-400 text-xs uppercase tracking-wider">Nom</span>
              <p className="text-white text-sm mt-0.5">{cvDraft.full_name}</p>
            </div>
          )}

          {cvDraft.title && (
            <div>
              <span className="text-slate-400 text-xs uppercase tracking-wider">Titre</span>
              <p className="text-white text-sm mt-0.5">{cvDraft.title}</p>
            </div>
          )}

          {cvDraft.summary && (
            <div>
              <span className="text-slate-400 text-xs uppercase tracking-wider">Résumé</span>
              <p className="text-white text-sm mt-0.5 leading-relaxed">{cvDraft.summary}</p>
            </div>
          )}

          {cvDraft.experiences && cvDraft.experiences.length > 0 && (
            <div>
              <span className="text-slate-400 text-xs uppercase tracking-wider">Expériences</span>
              <div className="space-y-3 mt-2">
                {cvDraft.experiences.map((exp, i) => (
                  <div key={i} className="border-l-2 border-violet-500/50 pl-3">
                    <div className="font-medium text-white text-sm">{exp.title}</div>
                    <div className="text-slate-400 text-xs">{exp.company} · {exp.period}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cvDraft.skills && cvDraft.skills.length > 0 && (
            <div>
              <span className="text-slate-400 text-xs uppercase tracking-wider">Compétences</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {cvDraft.skills.map((skill, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-violet-500/20 text-violet-300 rounded text-xs"
                  >
                    {typeof skill === "string" ? skill : skill.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-[#14183a] border border-slate-700/50 rounded-2xl p-6 text-center text-slate-400">
          <p className="text-4xl mb-3">📄</p>
          <p>Votre CV est en cours de préparation par notre équipe.</p>
          <p className="text-sm mt-1">Vous serez notifié(e) quand il sera prêt pour validation.</p>
        </div>
      )}

      {/* Validation CTA */}
      <div className="bg-[#14183a] border border-slate-700/50 rounded-2xl p-6">
        {alreadyValidated ? (
          <div className="text-center">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-semibold text-emerald-400">CV validé</p>
            <p className="text-slate-400 text-sm mt-1">
              Votre équipe Lenvia a été notifiée. Merci !
            </p>
          </div>
        ) : (
          <div>
            <h2 className="font-semibold text-white mb-2">Valider mon CV</h2>
            <p className="text-slate-400 text-sm mb-4">
              En validant, vous confirmez que les informations présentes dans votre CV sont exactes.
              Notre équipe sera immédiatement notifiée.
            </p>
            <button
              onClick={handleValidate}
              disabled={validating || !cvDraft}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50"
            >
              {validating ? "Validation en cours..." : "✓ Confirmer et valider mon CV"}
            </button>
          </div>
        )}
      </div>

      {/* Logout */}
      <div className="text-center">
        <button
          onClick={() => { logout(); navigate("/candidat/login"); }}
          className="text-slate-500 hover:text-slate-400 text-sm transition-colors"
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
